import React from 'react';
import PropTypes from 'prop-types';
import * as Styles from './Summary.styles';
import withErrorBoundary from '@/utils/errorBoundary';
import bannerCriticalPath from '../../media/banner-critical.svg';
import bannerSuccessPath from '../../media/banner-success.svg';
import bannerWarningPath from '../../media/banner-warning.svg';
import bannerGradientPath from '../../media/banner-gradient.svg';
import ProtectionCard, { PROTECTION_CARD_TYPES } from '../ProtectionCard';

import {
  AlertV2,
  IconCodeBold,
  IconFolderOutline,
  Skeleton,
} from 'gatorcomponents';

const Summary = ({
  testId = 'summary',
  content,
  showProtectionCard = false,
  protectionCardType,
  hideProtectionCardButtons = false,
  onProtectionCardAction,
  alerts = {
    showTrialActiveMessage: false,
    showCleaningInProgressMessage: false,
    showLimitedProtectionMessage: false,
    showFullProtectionActiveMessage: false,
    showExpiredProtectionMessage: false,
    showTeamInterventionMessage: false,
    showTeamInterventionResolvedMessage: false,
  },
  onOpenTicket,
  threatsNumber = 0,
  trialDaysLeft = 0,
  maliciousCodesRemoved = 0,
  compromisedFiles = 0,
  loading = false,
  clientIsAA,
}) => {
  const handleOnProtectionCardAction = (actionKey) => {
    if (onProtectionCardAction) {
      onProtectionCardAction(actionKey);
    }
  };

  return (
    <>
      {alerts.showTrialActiveMessage && (
        <Styles.StatusBanner type="success" data-testid="trial-active-message">
          <Styles.StatusIcon src={bannerSuccessPath} alt="Trial Active" />
          <span dangerouslySetInnerHTML={{ __html: content.alerts.trialActive(trialDaysLeft) }} />
        </Styles.StatusBanner>
      )}
      {alerts.showCleaningInProgressMessage && (
        <Styles.StatusBanner type="warning" data-testid="cleaning-in-progress-message">
          <Styles.StatusIcon src={bannerWarningPath} alt="Cleaning in Progress" />
          <span dangerouslySetInnerHTML={{ __html: content.alerts.cleaningInProgress }} />
        </Styles.StatusBanner>
      )}
      {alerts.showLimitedProtectionMessage && (
        <Styles.StatusBanner type="warning" data-testid="limited-protection-message">
          <Styles.StatusIcon src={bannerWarningPath} alt="Limited Protection" />
          <span dangerouslySetInnerHTML={{ __html: content.alerts.limitedProtection }} />
        </Styles.StatusBanner>
      )}
      {alerts.showFullProtectionActiveMessage && (
        <Styles.StatusBanner type="success-gradient" data-testid="full-protection-active-message">
          <Styles.StatusIcon src={bannerGradientPath} alt="Full Protection Active" />
          <span dangerouslySetInnerHTML={{ __html: content.alerts.fullProtectionActive }} />
        </Styles.StatusBanner>
      )}
      {alerts.showExpiredProtectionMessage && (
        <Styles.StatusBanner type="critical" data-testid="expired-protection-message">
          <Styles.StatusIcon src={bannerCriticalPath} alt="Expired Protection" />
          <span dangerouslySetInnerHTML={{ __html: content.alerts.expiredProtection }} />
        </Styles.StatusBanner>
      )}
      <Styles.Container data-testid={testId}>
        <Styles.Adapter>
          <Styles.TitleAndColumnsContainer>
            <Styles.Title>
              {content.title}
            </Styles.Title>

            <Styles.Content>
              <Styles.Column>
                <Styles.ColumnIcon>
                  <IconCodeBold />
                </Styles.ColumnIcon>
                <Styles.ColumnTextWrapper>
                  <Styles.ColumnTitle>
                    {content.maliciousCodesRemoved}
                  </Styles.ColumnTitle>
                  <Styles.ColumnDescription>
                    {content.maliciousCodesRemovedDescription}
                  </Styles.ColumnDescription>
                  <Styles.ColumnValue>
                    {loading ? <Skeleton height="40px" width="80px" /> : maliciousCodesRemoved}
                  </Styles.ColumnValue>
                </Styles.ColumnTextWrapper>
              </Styles.Column>
              <Styles.Column>
                <Styles.Divider />
              </Styles.Column>

              <Styles.Column>
                <Styles.ColumnIcon>
                  <IconFolderOutline />
                </Styles.ColumnIcon>
                <Styles.ColumnTextWrapper>
                  <Styles.ColumnTitle>
                    {content.compromisedFiles}
                  </Styles.ColumnTitle>
                  <Styles.ColumnDescription>
                    {content.compromisedFilesDescription}
                  </Styles.ColumnDescription>
                  <Styles.ColumnValue>
                    {loading ? <Skeleton height="40px" width="80px" /> : compromisedFiles}
                  </Styles.ColumnValue>
                </Styles.ColumnTextWrapper>
              </Styles.Column>
            </Styles.Content>
          </Styles.TitleAndColumnsContainer>

          {(showProtectionCard && !loading) && (
            <ProtectionCard
              content={content}
              type={protectionCardType}
              hideButtons={hideProtectionCardButtons}
              trialDaysLeft={trialDaysLeft}
              onAction={handleOnProtectionCardAction}
              clientIsAA={clientIsAA}
            />
          )}

          {showProtectionCard && loading && (
            <Skeleton
              width="534px"
              height="205px"
            />
          )}
        </Styles.Adapter>
        <Styles.AlertWrapper>
          {alerts.showTeamInterventionMessage && (
            <AlertV2
              title={content.alerts.teamIntervention.title(threatsNumber)}
              description={content.alerts.teamIntervention.description}
              primaryButtonLabel={content.alerts.teamIntervention.buttonLabel}
              onPrimaryAction={onOpenTicket}
              variant="critical"
            />
          )}
          {alerts.showTeamInterventionResolvedMessage && (
            <AlertV2
              title={content.alerts.teamInterventionResolved.title}
              description={content.alerts.teamInterventionResolved.description}
              primaryButtonLabel={content.alerts.teamInterventionResolved.buttonLabel}
              onPrimaryAction={onOpenTicket}
              variant="success"
            />
          )}
        </Styles.AlertWrapper>
      </Styles.Container>
    </>
  );
};

Summary.propTypes = {
  testId: PropTypes.string,
  showProtectionCard: PropTypes.bool,
  onProtectionCardAction: PropTypes.func,
  protectionCardType: PropTypes.oneOf(Object.values(PROTECTION_CARD_TYPES)),
  hideProtectionCardButtons: PropTypes.bool,
};


export default withErrorBoundary(Summary, 'Summary');
